import {
  FormulaPropertyDescriptor,
  PropertyDescriptor,
  PropertyDescriptorType,
} from "datasheets/model/core/Workbook";
import { addNewDescriptorForColumnName } from "datasheets/model/logic/columns";
import { viewAtom } from "datasheets/model/atoms/WorkbookFocusAtoms";
import { datasetAtom } from "datasheets/views/spreadsheet/state/datasetAtom";
import { atom } from "jotai";
import { SpreadsheetViewConfig } from "./SpreadsheetViewConfig";
import {
  dictionaryReverse,
  dictionaryMap,
} from "datasheets/model/utils/dictionaryUtils";
import { parse } from "datasheets/model/parsers/formula.js";

export type ColumnMappingHandler = (
  newName: string,
  newDescriptor: PropertyDescriptor
) => void;
interface ColumnMappingHandlerPayload {
  columnId: number;
  newName: string;
  newDescriptor: PropertyDescriptor;
}
function descriptorGuidToNames(
  descriptor: PropertyDescriptor,
  currentSheetGuidToName: { [key: string]: string }
): PropertyDescriptor {
  let newPropertyDescriptor: PropertyDescriptor = {
    type: descriptor.type,
  };
  if (descriptor.type === PropertyDescriptorType.Formula) {
    const formulaDesc = newPropertyDescriptor as FormulaPropertyDescriptor;
    const expression = (descriptor as FormulaPropertyDescriptor).expression;
    if (!!expression) {
      formulaDesc.expression = parse(expression, {
        type: "to_property_key",
        nameToKey: currentSheetGuidToName,
      });
    }
  }
  return { ...descriptor, ...newPropertyDescriptor };
}
export const columnMappingHandlerAtom = atom(
  // Get property descriptor as column names
  // Return value is map of column index to property descriptor
  (get) => {
    // Get properties
    const dataset = get(datasetAtom);
    const properties = dataset.properties;
    // Create  map of column guids to column index
    const view = get(viewAtom);
    const viewConfig = view.config as SpreadsheetViewConfig;
    const columnGuidToIdx = dictionaryReverse(viewConfig.columnMapping);
    // Map descriptors to return value
    const idxToDescriptor = dictionaryMap(
      properties.descriptors,
      (key) => columnGuidToIdx[key],
      (descriptor) => descriptorGuidToNames(descriptor, properties.keyToName)
    );
    return idxToDescriptor;
  },
  // Save property descriptor as column names to property descriptor as column ids
  (get, set, payload: ColumnMappingHandlerPayload) => {
    // Unpack payload
    const columnId = payload.columnId;
    const newName = payload.newName;
    // Get Atom values
    const prevDataset = get(datasetAtom);
    const prevView = get(viewAtom);
    const prevViewConfig = prevView.config as SpreadsheetViewConfig;
    // Create new view for updates
    const dataset = { ...prevDataset };
    // If name is being set, handle name change and create new descriptor if column was previously unused
    let propertyKey = "";
    let oldName = "";
    if (columnId in prevViewConfig.columnMapping) {
      propertyKey = prevViewConfig.columnMapping[columnId];
      oldName = prevDataset.properties.keyToName[propertyKey];
      if (oldName !== newName && newName in prevDataset.properties.nameToKey) {
        // Guard. Don't update anything if name already assigned to another column
        return;
      }
    } else {
      propertyKey = addNewDescriptorForColumnName(dataset.properties, newName);
      // Update column mapping
      const view = { ...prevView };
      const viewConfig = view.config as SpreadsheetViewConfig;
      viewConfig.columnMapping[columnId] = propertyKey;
      set(viewAtom, view);
    }
    // If name is changing, update all key references
    if (oldName !== newName) {
      delete dataset.properties.nameToKey[oldName];
      dataset.properties.keyToName[propertyKey] = newName;
      dataset.properties.nameToKey[newName] = propertyKey;
    }
    // If we have a new property descriptor, merge any changes in
    const newDescriptor = payload.newDescriptor;
    if (newDescriptor.type === PropertyDescriptorType.Formula) {
      const expression = (newDescriptor as FormulaPropertyDescriptor)
        .expression;
      const nameToKeyMap = dataset.properties.nameToKey;
      (newDescriptor as FormulaPropertyDescriptor).expression = parse(
        expression,
        {
          type: "to_property_key",
          nameToKey: nameToKeyMap,
        }
      );
    }
    const oldDescriptor = dataset.properties.descriptors[propertyKey];
    const propertyDescriptor = !!newDescriptor
      ? { ...oldDescriptor, ...newDescriptor }
      : oldDescriptor;
    dataset.properties.descriptors[propertyKey] = propertyDescriptor;
    set(datasetAtom, dataset);
  }
);
interface ColumnBeingEdited {
  columnName: string;
  columnId: number;
}

export const columnBeingEditedAtom = atom<ColumnBeingEdited | null>(null);
